import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useTheme } from './hooks/useTheme';
import { LanguageProvider } from './i18n/LanguageContext';
import { HOME_PATHS } from './utils/navActive';
import { cx } from './utils/cx';

import FaviconHandler from './components/FaviconHandler';
import Ambient from './components/Ambient';
import Nav from './components/Nav';
import TabBar from './components/TabBar';
import Footer from './components/Footer';

import Home from './pages/Home';
import AppDetailPage from './pages/AppDetailPage';
import LegalPage from './pages/LegalPage';
import NotFound from './pages/NotFound';

import OnyxAIDetail from './pages/OnyxAIDetail';
import LunicaAIDetail from './pages/LunicaAIDetail';
import OnyxAIPrivacy from './pages/OnyxAIPrivacy';
import OnyxAITerms from './pages/OnyxAITerms';
import LunicaAIPrivacy from './pages/LunicaAIPrivacy';
import LunicaAITerms from './pages/LunicaAITerms';

import styles from './App.module.css';

const groupKeyFor = (pathname) => (HOME_PATHS.includes(pathname) ? '__home__' : pathname);

// Cross-fades route changes by rendering `<Routes>` against a location that
// lags behind the real one for 220ms during the "out" phase — a hand-rolled
// equivalent of the reference's `view.classList.add('out')` + `setTimeout`
// swap, with no animation library. Navigation within the Home-owning path
// group (`/`, `/apps`, `/studio`, `/contact`) swaps instantly: Home itself
// handles the smooth in-page scroll, so there is nothing to cross-fade.
function RouteTransition() {
  const location = useLocation();
  const [displayedLocation, setDisplayedLocation] = useState(location);
  const [phase, setPhase] = useState('enter');

  useEffect(() => {
    if (location.pathname === displayedLocation.pathname) return;

    const samePageGroup = groupKeyFor(location.pathname) === groupKeyFor(displayedLocation.pathname);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (samePageGroup || reduceMotion) {
      setDisplayedLocation(location);
      return;
    }

    setPhase('out');
    const t = setTimeout(() => {
      setDisplayedLocation(location);
      setPhase('enter');
    }, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <main className={cx(styles.view, styles[phase])}>
      <Routes location={displayedLocation}>
        <Route path="/" element={<Home />} />
        <Route path="/apps" element={<Home />} />
        <Route path="/studio" element={<Home />} />
        <Route path="/contact" element={<Home />} />

        <Route path="/app/:appId" element={<AppDetailPage />} />
        <Route path="/app/:appId/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/app/:appId/terms" element={<LegalPage kind="terms" />} />

        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />

        {/* Cadet Pilot's App-Store-registered legacy URL — direct render, never a redirect */}
        <Route path="/aeropace" element={<AppDetailPage appId="cadet-pilot" />} />
        <Route path="/aeropace/privacy" element={<LegalPage appId="cadet-pilot" kind="privacy" />} />
        <Route path="/aeropace/terms" element={<LegalPage appId="cadet-pilot" kind="terms" />} />
        <Route path="/astryx" element={<Navigate to="/aeropace" replace />} />
        <Route path="/astryx/privacy" element={<Navigate to="/aeropace/privacy" replace />} />
        <Route path="/astryx/terms" element={<Navigate to="/aeropace/terms" replace />} />

        {/* Social Fridge Magnets isn't store-registered yet — safe to canonicalize */}
        <Route path="/socialfridgemagnets" element={<Navigate to="/app/social-fridge-magnets" replace />} />
        <Route path="/socialfridgemagnets/privacy" element={<Navigate to="/app/social-fridge-magnets/privacy" replace />} />
        <Route path="/socialfridgemagnets/terms" element={<Navigate to="/app/social-fridge-magnets/terms" replace />} />

        {/* OnyxAI / Lunica AI — kept exactly as-is, excluded from the main Apps list/nav */}
        <Route path="/onyxai" element={<OnyxAIDetail />} />
        <Route path="/onyxai/privacy" element={<OnyxAIPrivacy />} />
        <Route path="/onyxai/terms" element={<OnyxAITerms />} />
        <Route path="/lunicaai" element={<LunicaAIDetail />} />
        <Route path="/lunicaai/privacy" element={<LunicaAIPrivacy />} />
        <Route path="/lunicaai/terms" element={<LunicaAITerms />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
  );
}

// OnyxAI/Lunica AI are untouched legacy pages that already render their own
// navbar — showing the new floating glass Nav/TabBar on top of it would
// double up the navigation, so it's suppressed on their routes.
const LEGACY_OWN_NAV_PREFIXES = ['/onyxai', '/lunicaai'];

function AppShell() {
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const hasOwnNav = LEGACY_OWN_NAV_PREFIXES.some((p) => pathname.startsWith(p));

  return (
    <>
      <Toaster position="top-center" theme={theme === 'light' ? 'light' : 'dark'} richColors />
      <Ambient />
      <FaviconHandler />
      {!hasOwnNav && <Nav />}
      {!hasOwnNav && <TabBar />}
      <RouteTransition />
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <Router>
        <AppShell />
      </Router>
    </LanguageProvider>
  );
}
